import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { requestViewCart, setIsCartVisible, setPdpCartNotificationPopUp } from "../../../../store/slices/cartSlice";
import { constants } from "../../../../constants/pageConstants/pdpConstants";
import PopOver from "../../../atoms/popOver";
import CustomImage from "../../../atoms/customImage";
import Typography from "../../../atoms/typography";
import ButtonComponent from "../../../atoms/button";

/**
 * CartNotificationPopOver component displays a popover notification
 * for the cart with options to view the cart or proceed to checkout.
 *
 * @props handleCheckoutRedirect
 * @returns {React.ReactElement} The rendered component.
 */
const CartNotificationPopOver = ({ handleCheckoutRedirect }) => {
  const dispatch = useDispatch();
  const cartNotification = useSelector((state) => state.cart?.pdpCartNotificationPopUp);

  /**
   * Handles the click event for viewing the cart.
   * Dispatches actions to request the cart view and hide the notification pop-up.
   *
   * @function onViewCartClickHandler
   * @returns {void}
   */
  const onViewCartClickHandler = () => {
    dispatch(requestViewCart());
    dispatch(setPdpCartNotificationPopUp(false));
    dispatch(setIsCartVisible(true));
  };

  /**
   * Handles the click event for proceeding to checkout.
   * Dispatches actions to hide the notification pop-up and navigates the user to the checkout page based on their login status.
   *
   * @function onProceedToCheckoutHandler
   * @returns {void}
   */
  const onProceedToCheckoutHandler = () => {
    dispatch(setPdpCartNotificationPopUp(false));
    handleCheckoutRedirect();
  };
  return (
    <PopOver
      isVisible={cartNotification}
      classes="flex-shrink-0 rounded-lg left-[calc(50%-165px)] border border-[#F5F5F5] bg-white shadow-md"
      isBackdrop={false}
      topOffset={80}
      parentClass=""
    >
      <div className="mx-16 my-20 flex w-[297px] flex-col items-center gap-6">
        <CustomImage src="/icons/tick-circle.svg" width={40} height={40} alt="tick_circle" />
        <Typography
          variant="p"
          Tag="p"
          classes="text-grey-500 text-base mt-12 font-normal leading-5 text-16 [font-variant-numeric:lining-nums proportional-nums] [font-feature-settings:'dlig'_on]"
        >
          {constants.NOTIFICATION_FOR_CART}
        </Typography>
        <div className="bg-white mt-24 flex w-full items-start gap-[16px] border-t border-[#F5F5F5]">
          <ButtonComponent
            size="small"
            classes="text-center font-inter text-14 px-[11px] font-semibold leading-[18px]"
            buttonVariant="customOutlined"
            label={constants.VIEW_CART}
            onClick={onViewCartClickHandler}
            isRemoveExtraPadding={true}
          />
          <ButtonComponent
            size="small"
            classes="text-center font-inter text-14 px-[11px] font-semibold leading-[18px] whitespace-nowrap"
            buttonVariant="filled"
            label={constants.PROCEED_TO_CHECKOUT}
            onClick={onProceedToCheckoutHandler}
            isRemoveExtraPadding={true}
          />
        </div>
      </div>
    </PopOver>
  );
};

CartNotificationPopOver.propTypes = {
  handleCheckoutRedirect: PropTypes.func.isRequired,
};

export default CartNotificationPopOver;
